<template>
  <textarea
    ref="input"
    :class="[
      plaintext ? 'form-control-plaintext' : 'form-control',
      plaintext ? 'w-100' : '',
      size ? `form-control-${size}` : null,
      stateClass,
    ]"
    :style="computedStyle"
    :name="name"
    :id="computedID"
    :disabled="disabled"
    :required="required"
    :placeholder="placeholder"
    :autocomplete="autocomplete"
    :readonly="readonly || plaintext"
    :rows="rows"
    :wrap="wrap"
    :aria-required="required ? 'true' : null"
    :aria-invalid="computedAriaInvalid"
    :value="localValue"
    v-bind="$attrs"
    @input="onInput"
    @change="onChange"
  ></textarea>
</template>

<script>
function getComputedStyles(el) {
  return el && el.nodeType === Node.ELEMENT_NODE ? window.getComputedStyle(el) : {};
}

function isVisible(el) {
  return (
    el &&
    el.nodeType === Node.ELEMENT_NODE &&
    document.body.contains(el) &&
    el.getBoundingClientRect().height > 0 &&
    el.getBoundingClientRect().width > 0
  );
}

function guid() {
  return Math.random().toString(16).slice(2) + Date.now().toString(16);
}

export default {
  name: 'd-form-textarea',
  inheritAttrs: false,
  emits: ['update:modelValue', 'change', 'input'],
  props: {
    name: {
      type: String,
      default: null,
    },
    id: {
      type: String,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    state: {
      type: [Boolean, String],
      default: null,
      validator: (v) => ['valid', 'invalid', true, false, null].includes(v),
    },
    size: {
      type: String,
      default: null,
      validator: (v) => ['sm', 'lg', null].includes(v),
    },
    placeholder: {
      type: String,
      default: null,
    },
    autocomplete: {
      type: String,
      default: null,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    plaintext: {
      type: Boolean,
      default: false,
    },
    rows: {
      type: [Number, String],
      default: null,
    },
    wrap: {
      type: String,
      default: 'soft',
      validator: (v) => ['soft', 'hard', 'off'].includes(v),
    },
    noResize: {
      type: Boolean,
      default: false,
    },
    maxRows: {
      type: [Number, String],
      default: null,
    },
    modelValue: {
      type: [String, Number],
      default: '',
    },
    ariaInvalid: {
      type: [Boolean, String],
      default: false,
    },
  },
  data() {
    return {
      localValue: this.modelValue ?? '',
      el: null,
      generatedId: `dr-textarea-${guid()}`,
    };
  },
  mounted() {
    this.el = this.$refs.input;
  },
  computed: {
    computedID() {
      return this.id || this.generatedId;
    },
    computedStyle() {
      return {
        width: this.plaintext ? '100%' : null,
        height: this.computedHeight,
        resize: this.noResize ? 'none' : null,
      };
    },
    computedMinRows() {
      return Math.max(parseInt(this.rows, 10) || 2, 2);
    },
    computedMaxRows() {
      return Math.max(this.computedMinRows, parseInt(this.maxRows, 10) || 0);
    },
    computedHeight() {
      if (this.localValue === null || !this.el || !isVisible(this.el)) {
        return null;
      }

      const previousHeight = this.el.style.height;

      // NOTE: keeping original behavior (auto-size) but without Vue3 proxy warnings
      this.el.style.height = 'inherit';

      const computed = getComputedStyles(this.el);

      let lineHeight = parseFloat(computed.lineHeight);
      if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
        lineHeight = 16;
      }

      const computedHeight = parseInt(computed.height, 10);
      const minHeight = Number.isFinite(computedHeight)
        ? computedHeight
        : lineHeight * this.computedMinRows;

      const offset =
        (parseInt(computed.borderTopWidth, 10) || 0) +
        (parseInt(computed.paddingTop, 10) || 0) +
        (parseInt(computed.paddingBottom, 10) || 0) +
        (parseInt(computed.borderBottomWidth, 10) || 0);

      this.el.style.height = previousHeight;

      const rawRows = (this.el.scrollHeight - offset) / lineHeight;
      const rows = Math.min(
        Math.max(rawRows, this.computedMinRows),
        this.computedMaxRows ? this.computedMaxRows - 1 : rawRows
      );

      if (!String(this.localValue).trim()) {
        return `${minHeight}px`;
      }

      return `${Math.max(Math.ceil(rows * lineHeight + offset), minHeight)}px`;
    },
    computedAriaInvalid() {
      if (!this.ariaInvalid || this.ariaInvalid === 'false') {
        return this.computedState === false ? 'true' : null;
      }

      if (this.ariaInvalid === true) {
        return 'true';
      }

      return this.ariaInvalid;
    },
    computedState() {
      if (this.state === true || this.state === 'valid') {
        return true;
      }

      if (this.state === false || this.state === 'invalid') {
        return false;
      }

      return null;
    },
    stateClass() {
      if (this.computedState === true) {
        return 'is-valid';
      }

      if (this.computedState === false) {
        return 'is-invalid';
      }

      return null;
    },
  },
  watch: {
    modelValue(newVal) {
      const next = newVal ?? '';
      if (next !== this.localValue) {
        this.localValue = next;
      }
    },
  },
  methods: {
    onInput(e) {
      const next = e.target.value;
      this.localValue = next;
      this.$emit('update:modelValue', next);
      this.$emit('input', next);
    },
    onChange(e) {
      const next = e.target.value;
      this.$emit('update:modelValue', next);
      this.$emit('change', next);
    },
  },
};
</script>
